import { type Html, Ui } from './ui.js';

export interface TipContent {
  html: Html;
  anchor: Element;
  /** `side` sits beside the anchor, towards `towards`; `above` sits over it. Both fall back to whatever fits. */
  placement: 'above' | 'side';
  towards?: 'left' | 'right';
  wide?: boolean;
}
type Resolver = (hovered: Element[]) => TipContent | null;

const GAP = 8;
const MARGIN = 8;

/**
 * One floating tooltip for everything under `root`. Content is resolved from the elements under the pointer,
 * so it stays correct while the hovered elements re-render, and overlays above them don't block it.
 */
const attach = (root: HTMLElement, resolve: Resolver): { refresh: () => void } => {
  const tip = document.createElement('div');
  tip.className = 'tooltip';
  tip.setAttribute('role', 'tooltip');
  tip.hidden = true;
  document.body.append(tip);
  let pointer: { x: number; y: number } | null = null;
  let shown = '';

  const hide = () => {
    tip.hidden = true;
    tip.replaceChildren();
    shown = '';
  };

  const place = (content: TipContent) => {
    const a = content.anchor.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const clampX = (x: number) => Math.max(MARGIN, Math.min(x, vw - t.width - MARGIN));
    const clampY = (y: number) => Math.max(MARGIN, Math.min(y, vh - t.height - MARGIN));
    const centerX = clampX(a.left + a.width / 2 - t.width / 2);
    const right = { x: a.right + GAP, y: clampY(a.top), fits: a.right + GAP + t.width <= vw - MARGIN };
    const left = { x: a.left - GAP - t.width, y: clampY(a.top), fits: a.left - GAP - t.width >= MARGIN };
    const above = { x: centerX, y: a.top - GAP - t.height, fits: a.top - GAP - t.height >= MARGIN };
    const below = { x: centerX, y: a.bottom + GAP, fits: a.bottom + GAP + t.height <= vh - MARGIN };
    const sides = content.towards === 'left' ? [left, right] : [right, left];
    const order = content.placement === 'side' ? [...sides, below, above] : [above, below, ...sides];
    const pick = order.find(p => p.fits) ?? { x: centerX, y: clampY(below.y) };
    tip.style.transform = `translate(${Math.round(pick.x)}px, ${Math.round(pick.y)}px)`;
  };

  const refresh = () => {
    const hovered = pointer ? document.elementsFromPoint(pointer.x, pointer.y).filter(e => root.contains(e)) : [];
    const content = hovered.length ? resolve(hovered) : null;
    if (!content) return hide();
    const markup = content.html.toString();
    if (shown !== markup) {
      Ui.setHtml(tip, content.html);
      shown = markup;
    }
    tip.classList.toggle('wide', content.wide ?? false);
    tip.hidden = false;
    place(content);
  };

  const track = (e: PointerEvent) => {
    pointer = { x: e.clientX, y: e.clientY };
    refresh();
  };
  root.addEventListener('pointermove', track);
  root.addEventListener('pointerdown', track);
  root.addEventListener('pointerleave', e => {
    if (e.pointerType !== 'mouse') return;
    pointer = null;
    hide();
  });
  document.addEventListener('pointerdown', e => {
    if (e.target instanceof Node && root.contains(e.target)) return;
    pointer = null;
    hide();
  });
  window.addEventListener('scroll', () => (pointer && !tip.hidden ? refresh() : undefined), { passive: true });
  return { refresh };
};

/** A plain text tip for the first hovered element matching `selector` that carries its text in `data-tip`. */
const textTip = (hovered: Element[], selector = '[data-tip]'): TipContent | null => {
  const anchor = hovered.find(e => e instanceof HTMLElement && e.matches(selector) && e.dataset.tip);
  return anchor instanceof HTMLElement
    ? { html: Ui.h`<div class="tip-text">${anchor.dataset.tip}</div>`, anchor, placement: 'above' }
    : null;
};

export const Tooltip = { attach, textTip };
